import * as React from 'react';
import { css } from 'styled-components';
import {
  Box,
  Stack,
  Heading,
  Text,
  Button,
  FormGroup,
  InputGroup,
  InputLeftElement,
  InputText,
  InputMessage,
  Message,
  colors,
} from '@aksara-ui/core';
import { IconUser, IconMessage, IconBriefcase, IconTickRounded } from '@aksara-ui/icons';
import { FormikProps, Form, Formik, Field, FieldProps } from 'formik';
import get from 'lodash-es/get';
import * as yup from 'yup';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import { SignupFormValues } from '../../../interfaces';
import { register } from '../../../utils/api';
import { addRegistrationFormEvent } from '../../../utils/gtmDataLayer';

interface SignupFormProps {
  onSuccess?: (response: SignupFormValues) => void;
}

const CAPTCHA_ENABLED = process.env.GATSBY_CAPTCHA_ENABLED;
const HCAPTCHA_SITEKEY = process.env.GATSBY_HCAPTCHA_SITEKEY;

const initialValues: SignupFormValues = {
  username: '',
  email: '',
  account_type: '',
  company: '',
  recaptcha: '',
};

const validationSchema = yup.object().shape({
  username: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_]+$/, // allow letters, numbers, and underscores
      'Please enter a valid username. A username should only contain alphanumeric characters, hyphens and underscores (no spaces).'
    )
    .min(5, 'Username must be 5-15 characters long.')
    .max(15, 'Username must be 5-15 characters long.')
    .required('Username is required.'),
  email: yup.string().email('Invalid email format.').required('Email is required.'),
  account_type: yup.string().required('Account type is required.'),
  company: yup.string().required('Company/School name is required.'),
  recaptcha:
    CAPTCHA_ENABLED === 'true' && HCAPTCHA_SITEKEY
      ? yup.mixed().required('Please finish the captcha verification.')
      : yup.mixed(),
});

export const SignupForm: React.FC<SignupFormProps> = ({ onSuccess }) => {
  const captchaRef = React.useRef<HCaptcha>(null);

  return (
    <>
      <Box mb="xl">
        <Heading as="h3" fontFamily="brand" fontWeight={700} m={0} scale={800}>
          Create your free account
        </Heading>
      </Box>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          const { username, email, account_type, company, recaptcha } = values;
          setSubmitting(true);

          try {
            const response = await register(username, email, account_type, company, recaptcha);

            if (onSuccess && response) {
              addRegistrationFormEvent('succeedFormSubmission');
              onSuccess(values);
            }
          } catch (err) {
            addRegistrationFormEvent('failedFormSubmission');
            const defaultErr = 'Oops... something went wrong';
            const isRecaptcha = get(err, 'response.data.message.recaptcha');
            const errorMessage = isRecaptcha
              ? isRecaptcha['error-codes']
              : get(err, 'response.data.message', defaultErr);
            setStatus(`Failed to sign up: ${errorMessage}`);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
          setFieldTouched,
          touched,
          status,
          errors,
        }: FormikProps<SignupFormValues>) => (
          <Form>
            {!!status && (
              <FormGroup>
                <Message state="error" message={status} />
              </FormGroup>
            )}
            <Field name="username">
              {({ field, form }: FieldProps<SignupFormValues['username']>) => (
                <FormGroup>
                  <InputGroup>
                    <InputLeftElement>
                      <IconUser fill="currentColor" />
                    </InputLeftElement>
                    <InputText
                      type="text"
                      placeholder="Username"
                      autoFocus
                      {...field}
                      errors={form.touched.username && !!form.errors.username}
                    />
                  </InputGroup>
                  {form.touched.username && !!form.errors.username && (
                    <InputMessage variant="error">{form.errors.username}</InputMessage>
                  )}
                </FormGroup>
              )}
            </Field>
            <Field name="email">
              {({ field, form }: FieldProps<SignupFormValues['email']>) => (
                <FormGroup>
                  <InputGroup>
                    <InputLeftElement>
                      <IconMessage fill="currentColor" />
                    </InputLeftElement>
                    <InputText
                      type="email"
                      placeholder="Email address"
                      {...field}
                      errors={form.touched.email && !!form.errors.email}
                    />
                  </InputGroup>
                  {form.touched.email && !!form.errors.email && (
                    <InputMessage variant="error">{form.errors.email}</InputMessage>
                  )}
                </FormGroup>
              )}
            </Field>
            <Stack spacing="xs" mb="md">
              <Box>
                <Text scale={300} id="account-type-label">
                  What&apos;s your role?
                </Text>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr"
                gridGap="md"
                role="group"
                aria-labelledby="account-type-label"
              >
                <Field name="account_type" value="developers">
                  {({ field }: FieldProps<SignupFormValues['account_type']>) => (
                    <Box
                      as="label"
                      position="relative"
                      p="md"
                      backgroundColor={field.value === 'developers' ? 'blue01' : 'grey01'}
                      border="1px solid"
                      borderColor={field.value === 'developers' ? 'blue04' : 'grey04'}
                      borderRadius={4}
                      overflow="hidden"
                      css={css`
                        cursor: pointer;

                        &:hover {
                          border-color: ${colors.grey05};
                        }

                        &:focus,
                        &:focus-within,
                        &:active {
                          border-color: ${colors.blue06};
                          box-shadow: rgba(0, 111, 230, 0.4) 0px 0px 2px 1px, rgb(0, 111, 230) 0px 0px 0px 1px;
                        }
                      `}
                    >
                      <input
                        type="radio"
                        {...field}
                        value="developers"
                        css={css`
                          opacity: 0;

                          @supports (clip-path: polygon(0 0)) {
                            clip-path: polygon(0 0);
                          }
                        `}
                      />
                      <Box
                        display="flex"
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        alignItems="center"
                        justifyContent="space-between"
                        p="md"
                      >
                        <Text scale={300}>Professional</Text>
                        {field.value === 'developers' ? (
                          <IconTickRounded fill={colors.blue06} aria-hidden />
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width={24}
                            height={24}
                            aria-hidden
                          >
                            <Box width={20} height={20} border="1px solid" borderColor="grey04" borderRadius={20} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Field>
                <Field name="account_type" value="student">
                  {({ field }: FieldProps<SignupFormValues['account_type']>) => (
                    <Box
                      as="label"
                      position="relative"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p="md"
                      backgroundColor={field.value === 'student' ? 'blue01' : 'grey01'}
                      border="1px solid"
                      borderColor={field.value === 'student' ? 'blue04' : 'grey04'}
                      borderRadius={4}
                      overflow="hidden"
                      css={css`
                        cursor: pointer;

                        &:hover {
                          border-color: ${colors.grey05};
                        }

                        &:focus,
                        &:focus-within,
                        &:active {
                          border-color: ${colors.blue06};
                          box-shadow: rgba(0, 111, 230, 0.4) 0px 0px 2px 1px, rgb(0, 111, 230) 0px 0px 0px 1px;
                        }
                      `}
                    >
                      <input
                        type="radio"
                        {...field}
                        value="student"
                        css={css`
                          opacity: 0;

                          @supports (clip-path: polygon(0 0)) {
                            clip-path: polygon(0 0);
                          }
                        `}
                      />
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        p="md"
                      >
                        <Text scale={300}>Student</Text>
                        {field.value === 'student' ? (
                          <IconTickRounded fill={colors.blue06} aria-hidden />
                        ) : (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width={24}
                            height={24}
                            aria-hidden
                          >
                            <Box width={20} height={20} border="1px solid" borderColor="grey04" borderRadius={20} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Field>
              </Box>
              {touched.account_type && !!errors.account_type && (
                <InputMessage variant="error">{errors.account_type}</InputMessage>
              )}
            </Stack>
            {!!values.account_type && (
              <Field name="company">
                {({ field, form }: FieldProps<SignupFormValues['company']>) => (
                  <FormGroup>
                    <InputGroup>
                      <InputLeftElement>
                        <IconBriefcase fill="currentColor" />
                      </InputLeftElement>
                      <InputText
                        type="text"
                        placeholder={values.account_type === 'student' ? 'School' : 'Company'}
                        {...field}
                        errors={form.touched.company && !!form.errors.company}
                      />
                    </InputGroup>
                    {form.touched.company && !!form.errors.company && (
                      <InputMessage variant="error">{form.errors.company}</InputMessage>
                    )}
                  </FormGroup>
                )}
              </Field>
            )}
            {CAPTCHA_ENABLED === 'true' && HCAPTCHA_SITEKEY && (
              <FormGroup>
                <>
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITEKEY}
                    onExpire={() => {
                      setFieldValue('recaptcha', '');
                    }}
                    onVerify={token => {
                      setFieldValue('recaptcha', token);
                      setFieldTouched('recaptcha', true);
                    }}
                  />
                  {touched.recaptcha && !!errors.recaptcha && (
                    <InputMessage variant="error">{errors.recaptcha}</InputMessage>
                  )}
                </>
              </FormGroup>
            )}
            <Box mt="lg">
              <Button type="submit" variant="primary" block disabled={!(isValid && dirty)} isLoading={isSubmitting}>
                Sign Up
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};
